import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useLocation, NavLink, Outlet } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import {
  IconExpand,
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  useFilters,
  useGetList,
  Filters,
  SelectItemProps,
  usePrevious,
  PermissionEnum,
  IconSelected,
  IconArrowRight,
  PermissionDenied,
  SchemaFilterType,
  formatSearchParams,
  InputText,
  IconSearch,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearCorporateList, setHintVisible } from './store/slices/list';
import {
  corporateCustomersListColumns,
  corporateCustomersListSchema,
  corporateCustomersListFiltersSchema,
  corporateCustomersListColumnsLabels,
} from './variables/list';
import { CustomerProps } from '../customers/types';
import {
  useLazyGetCorporateUsersQuery,
  useGetCreateCorporateCustomerValidatorQuery,
} from './store/services/corporate';
import { useLazyGetCustomersApplicationSchemesQuery } from '../../store/dictionaries/services';
import formatCorporateCustomersList from './helpers/formate-corporate-customer-list';
import { clearGeneralState } from './store/slices/general-info';
import { clearOverviewState } from './store/slices/overview/overview';
import { clearDocumentsState } from '../customers/store/slices/documents/documents';
import { clearAmlState } from './store/slices/aml/aml';
import { clearAccountsState } from '../customers/store/slices/accounts-statements';
import { clearStatisticState } from '../customers/store/slices/statistic';
import {
  clearAccountsTransactionState,
  clearCardsTransactionState,
} from '../customers/store/slices/statements';
import { clearTariffsState } from '../customers/store/slices/tariffs/tariff';
import { clearExternalAccounts } from '../customers/store/slices/external-accounts';

import ROUTES from '../../routes';

import cn from '../individual/styles.module.css';

let timeout: NodeJS.Timeout;

const CorporateList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [countriesList, setCountriesList] = useState<
    SelectItemProps[] | undefined
  >(undefined);
  const [companyNameSearch, setCompanyNameSearch] = useState('');

  const {
    customersDictionaries: {
      incorporationDates,
      dictionariesIsLoading,
      applicationSchemes,
    },
    corporateCustomers: {
      list: {
        corporateCustomers,
        pagination,
        hintVisible,
        createdApplicationId,
      },
      info: {
        general: { baseInformation, generalInformation },
        overview: { baseInfo },
      },
    },
  } = useAppSelector((state) => state);
  const { countriesList: countries, activePermissions } = useAppSelector(
    (state) => state.root,
  );

  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const prevPagination = usePrevious(pagination);

  useGetCreateCorporateCustomerValidatorQuery();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    corporateCustomersListSchema,
  );

  const prevFilters = usePrevious(filters);

  const [getCorporateList, { isFetching, isLoading }] =
    useLazyGetCorporateUsersQuery();
  const [getCustomersApplicationSchemes] =
    useLazyGetCustomersApplicationSchemesQuery();

  const { getListAction, onSort, onFilter, setPage, errorList } =
    useGetList<CustomerProps>({
      getList: getCorporateList,
      onFilterChange,
      onSortChange,
      schema: corporateCustomersListSchema,
    });

  const onSearchAutocomplete = (name: string, value: string) => {
    const nextCountries = countries?.filter((item) =>
      item.name.toLowerCase().includes(value.toLowerCase()),
    );

    setCountriesList(nextCountries);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: corporateCustomersListFiltersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  const formattedData = useMemo(() => {
    const data = formatCorporateCustomersList(
      corporateCustomers as CustomerProps[],
      countries,
      incorporationDates,
    );

    return data;
  }, [corporateCustomers, filters, sort, countries]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (!countriesList && countries) {
      setCountriesList(countries);
    }
  }, [countries, countriesList]);

  useEffect(() => {
    if (countriesList) {
      setValues('countryOfIncorporationId', countriesList);
    }
  }, [countriesList]);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !corporateCustomers
    ) {
      let currentFilters = { ...filters };
      const currentFiltersStorage = localStorage.getItem(
        'corporateOrganizationsFilters',
      );

      if (currentFiltersStorage) {
        currentFilters = JSON.parse(currentFiltersStorage);

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.companyName) {
            setCompanyNameSearch(parsed.companyName as string);

            currentFilters.companyName = {
              value: [parsed.companyName as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!currentFilters.status) {
          currentFilters.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        onFilterChange(currentFilters);
      }

      if (!filters.status && !currentFiltersStorage) {
        const filtersValues: Filters = {
          ...currentFilters,
        };

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.companyName) {
            setCompanyNameSearch(parsed.companyName as string);

            currentFilters.companyName = {
              value: [parsed.companyName as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!filters.status) {
          filtersValues.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        onFilter({
          ...filtersValues,
        });
      } else if (!corporateCustomers) {
        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.companyName) {
            setCompanyNameSearch(parsed.companyName as string);

            currentFilters.companyName = {
              value: [parsed.companyName as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!currentFilters.status) {
          currentFilters.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        getListAction({
          filters: {
            ...currentFilters,
          },
        });
      }
    }
  }, [filters, prevFilters, corporateCustomers]);

  useEffect(() => {
    if (!corporateCustomers && search) {
      formatSearchParams({
        currentSchemaValues: corporateCustomersListSchema,
        storageKey: 'corporateOrganizationsFilters',
        search,
      });
    }
  }, [corporateCustomers, search]);

  useEffect(() => {
    const currentFilters = localStorage.getItem(
      'corporateOrganizationsFilters',
    );

    if (
      currentFilters &&
      !isEqual(prevPagination, pagination) &&
      !pagination?.hasPreviousPage
    ) {
      const parsed = JSON.parse(currentFilters);

      if (parsed.cursor) {
        delete parsed.cursor;

        localStorage.setItem(
          'corporateOrganizationsFilters',
          JSON.stringify({
            ...parsed,
          }),
        );
      }
    }
  }, [pagination, prevPagination]);

  const onClear = () => {
    dispatch(clearOverviewState());
    dispatch(clearAccountsState());
    dispatch(clearAmlState());
    dispatch(clearStatisticState());
    dispatch(clearAccountsTransactionState());
    dispatch(clearCardsTransactionState());
    dispatch(clearDocumentsState());
    dispatch(clearTariffsState());
    dispatch(clearExternalAccounts());
  };

  useEffect(() => {
    if (generalInformation || baseInformation) {
      dispatch(clearGeneralState());
    }

    if (baseInfo) {
      onClear();
    }
  }, [generalInformation, baseInfo, baseInformation]);

  useEffect(() => {
    dispatch(clearDocumentsState());
  }, []);

  useEffect(() => {
    if (
      !applicationSchemes &&
      activePermissions?.includes(PermissionEnum.APPLICATIONS_SCHEMES_LIST)
    ) {
      getCustomersApplicationSchemes();
    }
  }, [applicationSchemes, activePermissions]);

  useEffect(() => {
    dispatch(clearCorporateList());
  }, []);

  let content;

  if (isLoading || dictionariesIsLoading) {
    content = (
      <div className={cn.customersLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !isLoading &&
    countriesList &&
    activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_LIST)
  ) {
    content = (
      <Table
        columns={corporateCustomersListColumns}
        data={formattedData || []}
        onSort={(name) => onSort(name)}
        filters={filters}
        sort={sort}
        columnsLabels={corporateCustomersListColumnsLabels}
        onFilterChange={(values) => {
          const nextValues = {
            ...values,
          };

          if (companyNameSearch) {
            nextValues.companyName = {
              value: [companyNameSearch],
              type: SchemaFilterType.STRING,
            };
          }

          if (!nextValues.status) {
            nextValues.status = {
              value: ['ACTIVE', 'CLOSED'],
              type: SchemaFilterType.STRING,
            };
          }

          onFilter(nextValues);

          localStorage.setItem(
            'corporateOrganizationsFilters',
            JSON.stringify({
              ...nextValues,
            }),
          );
        }}
        hiddenFiltersTags={
          filters?.status?.value.length === 2 ? ['status'] : []
        }
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        tableFormClassName={cn.customersListForm}
        take="20"
        setPage={(data) => {
          const nextFilters: Filters = {
            ...filters,
          };

          if (companyNameSearch) {
            nextFilters.companyName = {
              value: [companyNameSearch],
              type: SchemaFilterType.STRING,
            };
          }

          setPage({ ...data, filters: nextFilters });

          localStorage.setItem(
            'corporateOrganizationsFilters',
            JSON.stringify({
              ...filters,
              cursor: {
                value: [data.id],
                type: SchemaFilterType.STRING,
              },
            }),
          );
        }}
        pagination={pagination}
        error={errorList}
        storeKey="corporate-organizations"
        withSettings
        clickable
      />
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <div className={cn.customersBottom} ref={scrollRef}>
      <div className={cn.customersTopContent}>
        <div className={cn.customerTopContentTitle}>Corporate Customers</div>
        <NavLink
          to={`${ROUTES.CUSTOMERS_CORPORATE_CREATE}${search || ''}`}
          className={cn.customersCreateLink}
        >
          <div className={cn.customersCreateLinkContent}>
            <IconExpand className={cn.customersCreateLinkIcon} />
            <span>Create Corporate Customers</span>
          </div>
        </NavLink>
        <InputText
          name="nameSearch"
          labelClassName={cn.nameSearch}
          value={companyNameSearch}
          placeholder="Enter company name"
          prefix={<IconSearch />}
          prefixClassName={cn.nameSearchIcon}
          onChange={(e) => {
            clearTimeout(timeout);

            setCompanyNameSearch(e.target.value);

            const nextFilters: Filters = {
              ...filters,
            };

            if (e.target.value) {
              nextFilters.companyName = {
                value: [e.target.value],
                type: SchemaFilterType.STRING,
              };
            } else if (!e.target.value && nextFilters.companyName) {
              delete nextFilters.companyName;
            }

            timeout = setTimeout(() => {
              onFilter({
                ...nextFilters,
              });
            }, 500);
          }}
        />
      </div>
      {content}
      <Outlet />
      {hintVisible && (
        <div className={cn.applicationCreatedSuccessful}>
          <div className={cn.applicationCreatedSuccessfulLeft}>
            <span>
              <IconSelected />
            </span>
            <span>Customer created</span>
          </div>
          <div className={cn.applicationCreatedSuccessfulRight}>
            <span>
              {createdApplicationId && (
                <NavLink
                  to={generatePath(
                    ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO,
                    {
                      applicationId: createdApplicationId,
                    },
                  )}
                >
                  <IconArrowRight />
                  Open application
                </NavLink>
              )}
            </span>
            <button
              type="button"
              className={cn.hintCloseButton}
              onClick={() => dispatch(setHintVisible(false))}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateList;
